import { useIntl } from "react-intl";
import { ICellRendererParams } from "ag-grid-community";
import { KTSVG, toAbsoluteUrl } from "../../../_metronic/helpers";
import { SimpleEntityGridColumn } from "../grid/EntityGrid";
import SVG from "react-inlinesvg";
import { Site } from "src/app/types/Site";

const cellRenderer = ({
    data,
    handlePBNModal,
}: ICellRendererParams & { handlePBNModal(isOpen: boolean, site: Site): void }) => {
    return (
        <>
            <a className="inline-action" href={`https://${data.domain}`} target="_blank">
                <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-2" />
            </a>
            <a
                className="inline-action"
                href={`https://majestic.com/reports/site-explorer/top-backlinks?MaxSourceUrlsPerRefDomain=1&removeDeleted=0&sortBy1=DomainTrustFlow&filters=NoFollow%28%220%22%29&showLinkGraph=0&q=${data.domain}&oq=${data.domain}&IndexDataSource=F#`}
                target="_blank"
            >
                <KTSVG path="/media/icons/personal/M.svg" className="svg-icon-2" />
            </a>
            <a
                className="inline-action"
                href={`https://fr.semrush.com/analytics/overview/?q=${data.domain}&protocol=https&searchType=domain`}
                target="_blank"
            >
                <KTSVG path="/media/icons/personal/S.svg" className="svg-icon-2" />
            </a>
            <span className="inline-action" onClick={() => handlePBNModal(true, data)}>
                <KTSVG path="/media/icons/duotune/general/gen062.svg" className="svg-icon-2" />
            </span>
        </>
    );
};

export default function useUpsertRDGridColumns(
    handlePBNModal: (isOpen: boolean, site: Site) => void
): Array<SimpleEntityGridColumn> {
    const { formatMessage } = useIntl();

    const cellClassRules = {
        "ttf-news": ({ data }: any) => data?.ttf?.startsWith("News"),
        "ttf-business": ({ data }: any) => data?.ttf?.startsWith("Business"),
        "ttf-reference": ({ data }: any) => data?.ttf?.startsWith("Reference"),
        "ttf-arts": ({ data }: any) => data?.ttf?.startsWith("Arts"),
        "ttf-recreation": ({ data }: any) => data?.ttf?.startsWith("Recreation"),
        "ttf-computers": ({ data }: any) => data?.ttf?.startsWith("Computers"),
        "ttf-society": ({ data }: any) => data?.ttf?.startsWith("Society"),
        "ttf-regional": ({ data }: any) => data?.ttf?.startsWith("Regional"),
        "ttf-home": ({ data }: any) => data?.ttf?.startsWith("Home"),
        "ttf-science": ({ data }: any) => data?.ttf?.startsWith("Science"),
        "ttf-health": ({ data }: any) => data?.ttf?.startsWith("Health"),
        "ttf-sports": ({ data }: any) => data?.ttf?.startsWith("Sports"),
        "ttf-world": ({ data }: any) => data?.ttf?.startsWith("World"),
        "ttf-shopping": ({ data }: any) => data?.ttf?.startsWith("Shopping"),
    };

    return [
        {
            field: "lang",
            headerName: "",
            pinned: "left",
            minWidth: 50,
            maxWidth: 50,
            cellRenderer: ({ data }: any) => {
                return (
                    <SVG
                        width={16}
                        src={toAbsoluteUrl(`/media/flags/${data.lang.toLowerCase()}.svg`)}
                    />
                );
            },
            filter: "agSetColumnFilter",
        },
        {
            field: "domain",
            headerName: formatMessage({ id: "sites.grid.col.domain" }),
        },
        {
            field: "isChecked",
            headerName: formatMessage({ id: "sites.grid.col.isChecked" }),
            maxWidth: 100,
            cellRenderer: ({ data }: any) =>
                data.isChecked ? (
                    <div className="w-100 h-100 d-flex justify-content-center">
                        <SVG
                            width="16px"
                            fill="var(--kt-text-success)"
                            src={toAbsoluteUrl("/media/icons/duotune/general/tick.svg")}
                        />
                    </div>
                ) : (
                    <div className="w-100 h-100 d-flex justify-content-center">
                        <SVG
                            fill="var(--kt-text-danger)"
                            width="16px"
                            src={toAbsoluteUrl("/media/icons/duotune/general/cross.svg")}
                        />
                    </div>
                ),
        },
        {
            field: "pbn",
            headerName: formatMessage({ id: "sites.grid.col.pbn" }),
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "thematic",
            headerName: formatMessage({ id: "sites.grid.col.thematic" }),
            minWidth: 140,
            maxWidth: 140,
        },
        {
            field: "price",
            headerName: formatMessage({ id: "sites.grid.col.price" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            valueFormatter: ({ data }: any) => `${data.price} €`,
            width: 80,
        },
        {
            field: "actions",
            sortable: false,
            filter: false,
            minWidth: 200,
            maxWidth: 200,
            cellRenderer,
            cellRendererParams: ({ data }: ICellRendererParams) => ({ data, handlePBNModal }),
        },
        {
            field: "da",
            headerName: formatMessage({ id: "sites.grid.col.da" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "tf",
            headerName: formatMessage({ id: "sites.grid.col.tf" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "ttf",
            headerName: formatMessage({ id: "sites.grid.col.ttf" }),
            cellClassRules,
        },
        {
            field: "cf",
            headerName: formatMessage({ id: "sites.grid.col.cf" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "rd",
            headerName: formatMessage({ id: "sites.grid.col.rd" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "traffic",
            headerName: formatMessage({ id: "sites.grid.col.traffic" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 100,
            maxWidth: 100,
            sort: "desc",
        },
        {
            field: "kw",
            headerName: formatMessage({ id: "sites.grid.col.kw" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "googleResult",
            headerName: formatMessage({ id: "sites.grid.col.google" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 100,
            maxWidth: 100,
        },
    ];
}
